import { useEffect, useRef } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { EpdSigningGroundSchema, EpdTitleStatus, SigningPerson } from '@app/models/orders';
import Api from '@app/services/api';
import { getResponseStatus } from '@app/services/apiErrors';
import { getCadesError, signBytes, withCachedKey } from '@app/services/cades';
import { ICertificate } from '@app/services/cades/interfaces';

import { ISigningResult, ISigningTarget } from '../interfaces';

interface ISigningParams {
  targets: ISigningTarget[];
  certificate: ICertificate;
  ground: EpdSigningGroundSchema;
  onProgress: (result: ISigningResult) => void;
}

export const useEpdSigning = () => {
  const client = useQueryClient();
  const active = useRef<AbortController>();
  const cancel = () => active.current?.abort();

  useEffect(() => () => active.current?.abort(), []);

  const mutation = useMutation({
    mutationFn: async ({ targets, certificate, ground, onProgress }: ISigningParams) => {
      const controller = new AbortController();
      active.current = controller;
      const { signal } = controller;
      const person: SigningPerson = ground.is_head
        ? {
            last_name: certificate.identity.lastName,
            first_name: certificate.identity.firstName,
            middle_name: certificate.identity.middleName || null,
            position: certificate.identity.position || null,
            poa_uid: ground.poa_uid,
          }
        : {
            last_name: ground.last_name || '',
            first_name: ground.first_name || '',
            middle_name: ground.middle_name,
            poa_uid: ground.poa_uid,
          };
      const signOne = async (target: ISigningTarget): Promise<ISigningResult | undefined> => {
        try {
          const job = await Api.orders.prepareEpdSigning(target.uid, signal);
          if (signal.aborted) return;
          const file = await Api.orders.prepareEpdSignature(target.uid, { job_uid: job.job_uid, person }, signal);
          if (signal.aborted) return;
          let signature: string;
          try {
            signature = await signBytes(certificate, file.content_base64, signal);
          } catch (error) {
            if (signal.aborted) return;
            return { uid: target.uid, status: 'error', pluginError: getCadesError(error) };
          }
          if (signal.aborted) return;
          try {
            const confirmed = await Api.orders.confirmEpdSignature(
              target.uid,
              { job_uid: job.job_uid, signature },
              signal
            );
            return { uid: target.uid, status: confirmed.status === EpdTitleStatus.Sent ? 'sent' : 'sending' };
          } catch (error) {
            const status = getResponseStatus(error);
            if (status === undefined || status === 0 || status >= 500) return { uid: target.uid, status: 'sending' };
            throw error;
          }
        } catch (error) {
          if (signal.aborted) return;
          return { uid: target.uid, status: 'error', error };
        }
      };
      const run = async () => {
        for (const target of targets) {
          if (signal.aborted) return;
          const result = await signOne(target);
          if (signal.aborted) return;
          if (result) onProgress(result);
        }
      };
      try {
        if (targets.length > 1) {
          await withCachedKey(certificate, run, signal);
        } else {
          await run();
        }
      } finally {
        active.current = undefined;
      }
    },
    retry: false,
    onSettled: () => client.invalidateQueries(['epd']),
  });
  return { ...mutation, cancel };
};
